// src/views/ComprarVenderView.jsx

import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Sparklines, SparklinesLine } from 'react-sparklines';
import TradingWidget from '../components/TradingWidget'; // Lo usaremos al final
import btcIcon from '../assets/btc-logo.png';
import ethIcon from '../assets/eth-logo.png';
import xcnIcon from '../assets/icono.png';
import { httpsCallable } from "firebase/functions";
import { functions } from '../firebaseConfig';
import VideoExplicativo from '../components/VideoExplicativo';

// --- (A) HEMOS SIMPLIFICADO EL COMPONENTE DE TARJETA ---
// Ya no busca sus propios datos, solo los recibe como props.
const CryptoChartCard = ({ symbol, name, icon, data, loading }) => {

  if (loading || !data) {
    return <div className="card stat-card"><h4>Cargando {symbol}...</h4></div>;
  }

  // Los datos (data) vienen del padre
  const tickerData = {
    price: data.priceCLPFormatted,
    change: data.changePercent,
    isPositive: data.isPositive,
  };
  const chartData = data.chartData; // Asumimos que el padre envía esto

  return (
    <div className="card stat-card-market">
      <h4><img src={icon} alt={`${name} logo`} className="coin-icon" />{name}({symbol})</h4>
      <p>{tickerData.price}</p>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span className={tickerData.isPositive ? 'positive' : 'negative'}>
          {tickerData.isPositive ? '+' : ''}{tickerData.change.toFixed(2)}%
        </span>
        <div className="mini-chart-container" style={{ width: '50%', height: '40px' }}>
          <Sparklines data={chartData}>
            <SparklinesLine color={tickerData.isPositive ? '#16c784' : '#ea3943'} />
          </Sparklines>
        </div>
      </div>
    </div>
  );
};

// --- (B) DATOS ESTÁTICOS DE XCOIN ---
// Lo definimos fuera para usarlo en el estado inicial
const xcoinStaticData = {
  name: 'XCoin',
  currency: 'XCO',
  price: 100000,
  change: 2.5,
  icon: xcnIcon,
  // Añadimos los campos que el backend devuelve para consistencia
  priceCLPFormatted: '$100.000',
  changePercent: 2.5,
  isPositive: true,
  chartData: [5, 10, 5, 20, 8, 15] // Datos de gráfico de ejemplo
};


const PRECIOS_URL = "https://southamerica-west1-xcoin-plataforma.cloudfunctions.net/obtenerPreciosConConversion";

const ComprarVenderView = () => {
  const location = useLocation();
  const tradeDetailsFromDashboard = location.state?.tradeDetails || {};

  // --- (C) EL ESTADO ES "LEVANTADO" AL PADRE ---
  const [allCryptoData, setAllCryptoData] = useState({
    'XCO': xcoinStaticData // Empezamos con los datos de XCoin
  });
  const [loading, setLoading] = useState(true);

  // --- (D) FETCH DATA FROM BACKEND (Supports BNB, USDT, BTC, ETH) ---
  useEffect(() => {
    const fetchMarketData = async () => {
      setLoading(true);
      try {
        const symbolsToFetch = ['BTC', 'ETH', 'USDT', 'BNB'];
        const response = await fetch(PRECIOS_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ data: { symbols: symbolsToFetch } })
        });

        let preciosApi = [];
        if (response.ok) {
          const result = await response.json();
          preciosApi = result.data || [];
        } else {
          console.warn("Backend prices failed, trying fallbacks...");
        }

        const newCryptoData = { ...allCryptoData };

        // --- FALLBACK CHECK FOR USDT ---
        let usdtItem = preciosApi.find(p => p.symbol === 'USDT');
        // If USDT missing or invalid price, try fallback
        if (!usdtItem || !usdtItem.priceInClp) {
          try {
            console.log("Fetching USDT from Buda Fallback (ComprarVenderView)...");
            const res = await fetch('/buda-api/v2/markets/usdt-clp/ticker');
            if (res.ok) {
              const data = await res.json();
              const price = parseFloat(data.ticker.last_price[0]);
              usdtItem = { symbol: 'USDT', priceInClp: price, changePercent: parseFloat(data.ticker.price_variation_24h || 0) * 100 };
              preciosApi.push(usdtItem);
            }
          } catch (e) {
            console.error("USDT Fallback failed", e);
          }
        }

        // 1. Process USDT & XCO
        if (usdtItem && usdtItem.priceInClp) {
          const usdtPrice = usdtItem.priceInClp;
          const usdtChange = usdtItem.changePercent || 0;

          // XCO
          const xcoPrice = usdtPrice * 10;
          newCryptoData['XCO'] = {
            name: 'XCoin',
            currency: 'XCO',
            icon: xcnIcon,
            priceCLPFormatted: `$${parseInt(xcoPrice).toLocaleString('es-CL')}`,
            price: xcoPrice,
            changePercent: 2.5, // Mock or fetch if available
            isPositive: true,
            chartData: [xcoPrice * 0.99, xcoPrice * 1.01, xcoPrice * 0.98, xcoPrice]
          };

          // USDT
          newCryptoData['USDT'] = {
            name: 'Tether',
            currency: 'USDT',
            icon: 'https://cryptologos.cc/logos/tether-usdt-logo.png?v=024',
            priceCLPFormatted: `$${parseInt(usdtPrice).toLocaleString('es-CL')}`,
            price: usdtPrice,
            changePercent: usdtChange,
            isPositive: usdtChange >= 0,
            chartData: [usdtPrice * 0.995, usdtPrice * 1.005, usdtPrice * 0.998, usdtPrice]
          };
        }

        // 2. Other Cryptos
        preciosApi.forEach(coin => {
          if (coin.symbol === 'BTC') {
            newCryptoData['BTC'] = {
              name: 'Bitcoin',
              currency: 'BTC',
              icon: btcIcon,
              priceCLPFormatted: `$${parseInt(coin.priceInClp).toLocaleString('es-CL')}`,
              price: coin.priceInClp,
              changePercent: coin.changePercent || 1.5,
              isPositive: (coin.changePercent || 0) >= 0,
              chartData: [coin.priceInClp * 0.98, coin.priceInClp * 1.02, coin.priceInClp]
            };
          }
          if (coin.symbol === 'ETH') {
            newCryptoData['ETH'] = {
              name: 'Ethereum',
              currency: 'ETH',
              icon: ethIcon,
              priceCLPFormatted: `$${parseInt(coin.priceInClp).toLocaleString('es-CL')}`,
              price: coin.priceInClp,
              changePercent: coin.changePercent || 1.2,
              isPositive: true,
              chartData: [coin.priceInClp * 0.99, coin.priceInClp * 1.01, coin.priceInClp]
            };
          }
          if (coin.symbol === 'BNB') {
            newCryptoData['BNB'] = {
              name: 'BNB',
              currency: 'BNB',
              icon: 'https://cryptologos.cc/logos/binance-coin-bnb-logo.png?v=024',
              priceCLPFormatted: `$${parseInt(coin.priceInClp).toLocaleString('es-CL')}`,
              price: coin.priceInClp,
              changePercent: coin.changePercent || 0.8,
              isPositive: true,
              chartData: [coin.priceInClp * 0.99, coin.priceInClp * 1.01, coin.priceInClp]
            };
          }
        });

        setAllCryptoData(newCryptoData);

      } catch (error) {
        console.error("Error fetching market data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMarketData();
  }, []);

  const handleTradeSubmit = (tradeDetails) => {
    alert(`¡Orden Lista! Detalles: ${JSON.stringify(tradeDetails, null, 2)}`);
  };

  return (
    <div className="comprar-vender-view">
      <div className="stats-row-market">
        {/* (E) Pasamos los datos como props a las tarjetas */}
        <CryptoChartCard
          symbol="XCO"
          name="XCoin"
          icon={xcnIcon}
          data={allCryptoData['XCO']}
          loading={false}
        />
        <CryptoChartCard
          symbol="BTC"
          name="Bitcoin"
          icon={btcIcon}
          data={allCryptoData['BTC']}
          loading={loading}
        />
        <CryptoChartCard
          symbol="ETH"
          name="Ethereum"
          icon={ethIcon}
          data={allCryptoData['ETH']}
          loading={loading}
        />
        <CryptoChartCard
          symbol="BNB"
          name="BNB"
          icon={'https://cryptologos.cc/logos/binance-coin-bnb-logo.png?v=024'}
          data={allCryptoData['BNB']}
          loading={loading}
        />
        <CryptoChartCard
          symbol="USDT"
          name="Tether"
          icon={'https://cryptologos.cc/logos/tether-usdt-logo.png?v=024'}
          data={allCryptoData['USDT']}
          loading={loading}
        />
      </div>

      {/* Video Explicativo del Servicio */}
      <div style={{ textAlign: 'center', margin: '20px 0' }}>
        <VideoExplicativo videoId="K6JryV2Dlo8" titulo="Ver video explicativo" />
      </div>

      {/* (F) ¡LE PASAMOS TODOS LOS DATOS AL WIDGET! */}
      <TradingWidget
        cryptoData={allCryptoData}
        initialState={tradeDetailsFromDashboard}
        onSubmit={handleTradeSubmit}
      />
    </div>
  );
};

export default ComprarVenderView;